"use strict";
var __knownSymbol = (name, symbol) => (symbol = Symbol[name]) ? symbol : Symbol.for("Symbol." + name);
var __typeError = (msg) => {
  throw TypeError(msg);
};
var __using = (stack, value, async) => {
  if (value != null) {
    if (typeof value !== "object" && typeof value !== "function") __typeError("Object expected");
    var dispose, inner;
    if (async) dispose = value[__knownSymbol("asyncDispose")];
    if (dispose === void 0) {
      dispose = value[__knownSymbol("dispose")];
      if (async) inner = dispose;
    }
    if (typeof dispose !== "function") __typeError("Object not disposable");
    if (inner) dispose = function() {
      try {
        inner.call(this);
      } catch (e) {
        return Promise.reject(e);
      }
    };
    stack.push([async, dispose, value]);
  } else if (async) {
    stack.push([async]);
  }
  return value;
};
var __callDispose = (stack, error, hasError) => {
  var E = typeof SuppressedError === "function" ? SuppressedError : function(e, s, m, _) {
    return _ = Error(m), _.name = "SuppressedError", _.error = e, _.suppressed = s, _;
  };
  var fail = (e) => error = hasError ? new E(e, error, "An error was suppressed during disposal") : (hasError = true, e);
  var next = (it) => {
    while (it = stack.pop()) {
      try {
        var result = it[1] && it[1].call(it[2]);
        if (it[0]) return Promise.resolve(result).then(next, (e) => (fail(e), next()));
      } catch (e) {
        fail(e);
      }
    }
    if (hasError) throw error;
  };
  return next();
};
/**
 * @license
 * Copyright 2024 Google Inc.
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { read, stat } from "#src/kvstore/http/read.js";
import {
  getS3BucketListing,
  listS3CompatibleUrl
} from "#src/kvstore/s3/list.js";
import { joinBaseUrlAndPath } from "#src/kvstore/url.js";
import { fetchOk } from "#src/util/http_request.js";
import { ProgressSpan } from "#src/util/progress_listener.js";
export class ReadableS3KvStore {
  constructor(sharedKvStoreContext, baseUrl, baseUrlForDisplay, knownToBeVirtualHostedStyle, fetchOkImpl = fetchOk) {
    this.sharedKvStoreContext = sharedKvStoreContext;
    this.baseUrl = baseUrl;
    this.baseUrlForDisplay = baseUrlForDisplay;
    this.knownToBeVirtualHostedStyle = knownToBeVirtualHostedStyle;
    this.fetchOkImpl = fetchOkImpl;
  }
  stat(key, options) {
    const url = joinBaseUrlAndPath(this.baseUrl, key);
    return stat(this, key, url, options, this.fetchOkImpl);
  }
  read(key, options) {
    const url = joinBaseUrlAndPath(this.baseUrl, key);
    return read(this, key, url, options, this.fetchOkImpl);
  }
  list(prefix, options) {
    var _stack = [];
    try {
      const { progressListener } = options;
      const _span = __using(_stack, progressListener === void 0 ? void 0 : new ProgressSpan(progressListener, {
        message: `Listing prefix ${this.getUrl(prefix)}`
      }));
      if (this.knownToBeVirtualHostedStyle) {
        return getS3BucketListing(
          this.baseUrl,
          prefix,
          this.fetchOkImpl,
          options
        );
      }
      return listS3CompatibleUrl(
        joinBaseUrlAndPath(this.baseUrl, prefix),
        this.baseUrlForDisplay,
        this.sharedKvStoreContext.chunkManager.memoize,
        this.fetchOkImpl,
        options
      );
    } catch (_) {
      var _error = _, _hasError = true;
    } finally {
      __callDispose(_stack, _error, _hasError);
    }
  }
  getUrl(path) {
    return joinBaseUrlAndPath(this.baseUrlForDisplay, path);
  }
  get supportsOffsetReads() {
    return true;
  }
  get supportsSuffixReads() {
    return true;
  }
}
function amazonS3Provider(sharedKvStoreContext, s3KvStoreClass) {
  return {
    scheme: "s3",
    description: "S3 (anonymous)",
    getKvStore(url) {
      const m = (url.suffix ?? "").match(/^\/\/([^/]+)(\/.*)?$/);
      if (m === null) {
        throw new Error("Invalid URL, expected `s3://<bucket>/<path>`");
      }
      const [, bucket, path] = m;
      return {
        store: new s3KvStoreClass(
          sharedKvStoreContext,
          `https://${bucket}.s3.amazonaws.com/`,
          `s3://${bucket}/`,
          /*knownToBeVirtualHostedStyle=*/
          true
        ),
        path: decodeURIComponent((path ?? "").substring(1))
      };
    }
  };
}
function s3Provider(sharedKvStoreContext, httpScheme, s3KvStoreClass) {
  return {
    scheme: `s3+${httpScheme}`,
    description: `S3-compatible ${httpScheme} server`,
    getKvStore(url) {
      const m = (url.suffix ?? "").match(/^\/\/([^/]+)(\/.*)?$/);
      if (m === null) {
        throw new Error(
          "Invalid URL, expected `s3+${httpScheme}://<host>/<path>`"
        );
      }
      const [, host, path] = m;
      return {
        store: new s3KvStoreClass(
          sharedKvStoreContext,
          `${httpScheme}://${host}/`,
          `s3+${httpScheme}://${host}/`,
          /*knownToBeVirtualHostedStyle=*/
          false
        ),
        path: decodeURIComponent((path ?? "").substring(1))
      };
    }
  };
}
export function registerProviders(registry, s3KvStoreClass) {
  registry.registerBaseKvStoreProvider(
    (context) => amazonS3Provider(context, s3KvStoreClass)
  );
  for (const httpScheme of ["http", "https"]) {
    registry.registerBaseKvStoreProvider(
      (context) => s3Provider(context, httpScheme, s3KvStoreClass)
    );
  }
}
